import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { userDetail } from 'Stores/userDetail';
import * as CONSTANTS from 'Utils/constants';
import Utils from 'Utils/utils';
import {
  suppressionListUploadState,
  SuppressionListUploadPopup,
} from 'Components/common/suppressionListUploadPopup';
import LoadingWrapper from 'Components/common/loadingWrapper';
import { toasterState } from 'Components/common/toaster';
import upgradeCommonImg from 'Assets/png/upgrade-common.png';

import { ReactComponent as DeleteIcon } from 'Assets/svg/delete-to.svg';
import { ReactComponent as DownloadIcon } from 'Assets/svg/download-icon.svg';
import emptyListIcon from 'Assets/png/emptySuppressionList.png';
import Button from 'Components/common/Button';

const frequentlyAskedQuestions = [
  {
    indexNo: '01',
    question: 'What are suppression lists?',
    answer:
      'Suppression lists provide the capability to exclude contacts from your search results. This is useful to omit contacts you already have from search results.',
  },
  {
    indexNo: '02',
    question:
      'Are there any limits on the number of suppressed emails or company domains?',
    answer:
      'Yes. Each account can exclude up to 500k email-ids and 100k domains. Each list can hold up to 25k entries.',
  },
  {
    indexNo: '03',
    question:
      'Are my suppression lists shared across my team&apos;s licences? ',
    answer: 'Yes. The suppression lists are shared across the team.',
  },
];

const FrequentlyAskedSection = () => {
  return (
    <div className="frequently-asked-section">
      <div className="title">Frequently Asked Questions</div>
      <div className="questions-list">
        {frequentlyAskedQuestions.map((item, index) => {
          const { indexNo, question, answer } = item;
          return (
            <div className="question-item" key={`frequet-question${index}`}>
              <div className="left-wrap">
                <div className="round-wrap">{indexNo}</div>
                <div className="line-wrap" />
              </div>
              <div className="right-wrap">
                <div
                  className="question-title"
                  dangerouslySetInnerHTML={{ __html: question }}
                />
                <div
                  className="question-answer"
                  dangerouslySetInnerHTML={{ __html: answer }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EmptyListFound = props => {
  const [
    enterpriseInterestApiLoading,
    setEnterpriseInterestApiLoading,
  ] = useState(false);
  const handleEnterprisePlanClick = () => {
    setEnterpriseInterestApiLoading(true);
    if (!enterpriseInterestApiLoading) {
      Utils.mixpanelTrack(
        CONSTANTS.MX_PRICING_PAGE.CLICK_LB_PRICING_TALK_TO_US,
        {},
      );
      const callback = () => {
        setEnterpriseInterestApiLoading(false);
      };
      Utils.enterprisePlanInterestNotification('SUPPRESSION_LIST', callback);
    }
  };
  return (
    <div className="empty-list-wrap">
      {Utils.isSuppressionListEnabled() ? (
        <div className="empty-list-section">
          <img
            className="no-results-logo"
            src={emptyListIcon}
            alt="No results found"
          />
          <div className="no-results-text">
            You don&apos;t have any suppression lists. <br />
            Please create one using the &quot;Add List&quot; button on top.
          </div>
        </div>
      ) : (
        <div className="upgrade-list-section">
          <img
            className="no-results-logo"
            src={upgradeCommonImg}
            alt="upgradeCommonImg "
          />
          <div className="no-results-text">
            <b>
              Exclude contacts you own from other sources using Suppression
              lists.
            </b>{' '}
            <br />
            Unlock this feature by upgrading to our enterprise plan.
          </div>
          <Button
            id="buyNow"
            btnText="Yes, I am interested"
            type="button"
            className={`action-btn ${
              enterpriseInterestApiLoading ? ' loading' : ''
            }`}
            loader={enterpriseInterestApiLoading}
            click={e => handleEnterprisePlanClick()}
          />
        </div>
      )}
      <FrequentlyAskedSection />
    </div>
  );
};

const SuppressionListHeader = observer(() => {
  const addNewSuppressionList = () => {
    if (Utils.isSuppressionListEnabled()) {
      Utils.mixpanelTrack(
        CONSTANTS.MX_SUPPRESSION_LIST_EVENTS.LB_SL_ADD_LIST,
        {},
      );
      suppressionListUploadState.setShowCreateList(true);
    } else {
      Utils.mixpanelTrack(CONSTANTS.MX_AS_EVENTS.AS_PAYWALL_PRICING_POPUP, {
        btnSource: 'SUPPRESSION_LIST',
      });
      Utils.showUserPricingPopup(true, '', true);
    }
  };
  return (
    <div className="suppression-list-header">
      <div className="page-title-text">Suppression Lists</div>
      {Utils.isSuppressionListEnabled() ? (
        <div className="add-new-list" onClick={addNewSuppressionList}>
          Add List
        </div>
      ) : (
        ''
      )}
    </div>
  );
});

const SuppressionList = observer(props => {
  const { getListLoading, suppressionListEntries } = suppressionListUploadState;

  const [
    enableOrDisableListLoadingIds,
    setEnableOrDisableListLoadingIds,
  ] = React.useState([]);

  React.useEffect(() => {
    if (Utils.isSuppressionListEnabled() && !getListLoading)
      suppressionListUploadState.getSuppressionList();
  }, []);

  const enableOrDisableList = (listId, status, actionType, listName) => {
    if (!enableOrDisableListLoadingIds[listId])
      if (status === 'COMPLETE') {
        const enableDisableListCallback = response => {
          if (response !== null) {
            const newList = suppressionListEntries.map(item => {
              const newObj = item;
              if (item.listId === listId) newObj.enabled = actionType;
              return item;
            });

            const msg = actionType
              ? `<b>${listName}</b> list will be excluded from all search.`
              : `<b>${listName}</b> list is disabled.`;
            suppressionListUploadState.setSuppressionListEntries(newList);

            toasterState.setToastMsg(msg, 'success');
          } else {
            toasterState.setToastMsg(
              'Update failed. Please try again later.',
              'failure',
            );
          }
          const listLoadingIds = enableOrDisableListLoadingIds.filter(
            e => e !== listId,
          );
          setEnableOrDisableListLoadingIds(listLoadingIds);
        };
        const listLoadingIds = enableOrDisableListLoadingIds;
        listLoadingIds.push(listId);
        setEnableOrDisableListLoadingIds(listLoadingIds);
        suppressionListUploadState.enableDisableList(
          listId,
          actionType ? 'enable' : 'disable',
          enableDisableListCallback,
        );
      } else {
        const msg = `<b>${listName}</b> list is in progress, You can enable it once its processed`;
        toasterState.setToastMsg(msg, 'failure');
      }
  };

  const downloadList = (e, listId, listName) => {
    const downloadListCallback = response => {
      if (response !== null) {
        Utils.downloadToCsv(response, listName);

        const msg = 'List downloaded successfully.';
        toasterState.setToastMsg(msg, 'success');
      } else {
        toasterState.setToastMsg(
          'Download failed. Please try again later.',
          'failure',
        );
      }
    };
    suppressionListUploadState.downloadList(listId, downloadListCallback);
  };
  const deleteList = (e, listId, listName) => {
    const deleteListCallback = response => {
      if (response !== null) {
        const newList = Utils.copyObjectWithoutReference(
          suppressionListEntries,
        );
        let listIndex = -1;
        // eslint-disable-next-line array-callback-return
        suppressionListEntries.map((item, index) => {
          if (item.listId === listId) listIndex = index;
        });
        if (listIndex > -1) newList.splice(listIndex, 1);

        const msg = 'Suppression list deleted successfully.';
        suppressionListUploadState.setSuppressionListEntries(newList);

        toasterState.setToastMsg(msg, 'success');
      } else {
        toasterState.setToastMsg(
          'Delete failed. Please try again later.',
          'failure',
        );
      }
    };
    suppressionListUploadState.deleteList(listId, deleteListCallback);
  };
  return (
    <div className="suppressionListPage">
      <SuppressionListUploadPopup />
      <SuppressionListHeader />
      {getListLoading ? (
        <LoadingWrapper show={getListLoading} />
      ) : Utils.isSuppressionListEnabled() &&
        suppressionListEntries.length > 0 ? (
        <div className="suppression-list-wrapper">
          <div className="contact-list">
            <div className="contact-list-wrap">
              <div className="contact-header">
                <div className="contact-header-item">List Name</div>
                <div className="contact-header-item">Type</div>
                <div className="contact-header-item">List Size</div>
                <div className="contact-header-item">Created By</div>
                <div className="contact-header-item align-center">Actions</div>
                <div className="contact-header-item align-center">Status</div>
              </div>
              {suppressionListEntries.map((item, index) => {
                const {
                  listName,
                  type,
                  numOfEntries,
                  createdBy,
                  enabled,
                  status,
                  listId,
                } = item;
                return (
                  <div className="contact-item" key={listId}>
                    <div className="contact-header-item">
                      {Utils.upperCaseFirstCharacter(listName)}
                    </div>
                    <div className="contact-header-item list-type">
                      {type.toLowerCase()}
                    </div>
                    <div className="contact-header-item">
                      {Utils.usNumberFormat(numOfEntries)}
                    </div>
                    <div className="contact-header-item created-name">
                      {createdBy !== null ? createdBy : '-'}
                    </div>
                    <div className="contact-header-item list-status">
                      {status === 'INPROGRESS' ? (
                        '-'
                      ) : (
                        <div className="action-icons">
                          <DownloadIcon
                            className="svg-icon"
                            title="Remove User"
                            onClick={e => downloadList(e, listId, listName)}
                          />
                          <DeleteIcon
                            className="svg-icon"
                            title="Remove User"
                            onClick={e => deleteList(e, listId, listName)}
                          />
                        </div>
                      )}
                    </div>
                    <div className="contact-header-item toggle-btn">
                      {status === 'COMPLETE' && (
                        <div className="toggleBtnSection">
                          <div className="toogleSequence">
                            <div className="toogleButton">
                              <input
                                type="checkbox"
                                className="toggleCheckbox"
                                checked={enabled}
                                onChange={e =>
                                  enableOrDisableList(
                                    listId,
                                    status,
                                    !enabled,
                                    listName,
                                  )
                                }
                              />
                              <div className="knobs" />
                              <div className="layer" />
                            </div>
                          </div>
                        </div>
                      )}

                      {status === 'INPROGRESS' && <span>IN PROGRESS</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="suppression-list-wrapper">
          <EmptyListFound />
        </div>
      )}
    </div>
  );
});

export default SuppressionList;
export { SuppressionList };
